import type {
  AliveAppsNotification,
  AliveAppState,
  AliveAppStatus,
  GetAliveAppsNotificationParams,
  WebExtensionNotificationRule,
} from '../types'

const DESKTOP_AGENT_UNINSTALLED_NOTIFICATION: AliveAppsNotification = {
  level: 'error',
  app: 'desktopAgent',
  status: 'uninstall',
  message:
    'The Desktop Agent is not installed. Please install immediately to ensure desktop activities are captured.',
}

const DESKTOP_AGENT_SIGNED_OUT_NOTIFICATION: AliveAppsNotification = {
  level: 'error',
  app: 'desktopAgent',
  status: 'offline',
  message:
    'The Desktop Agent is not signed in. Please sign in immediately to ensure desktop activities are captured.',
}

const WEB_EXTENSION_NOTIFICATION_RULES: WebExtensionNotificationRule[] = [
  {
    chrome: { installed: false, signedIn: false },
    edge: { installed: false, signedIn: false },
    notification: {
      level: 'error',
      app: 'webExtension',
      status: 'uninstall',
      settingsTab: 'chrome',
      message:
        'Either Chrome or Edge web extension is not installed. Please install one immediately to ensure web activities are captured.',
    },
  },
  {
    chrome: { installed: true, signedIn: false },
    edge: { installed: false, signedIn: false },
    notification: {
      level: 'error',
      app: 'webExtension',
      status: 'offline',
      settingsTab: 'chrome',
      message:
        'The Chrome extension is not signed in. Please sign in immediately to ensure web activities are captured.',
    },
  },
  {
    chrome: { installed: false, signedIn: false },
    edge: { installed: true, signedIn: false },
    notification: {
      level: 'error',
      app: 'webExtension',
      status: 'offline',
      settingsTab: 'edge',
      message:
        'The Edge extension is not signed in. Please sign in immediately to ensure web activities are captured.',
    },
  },
  {
    chrome: { installed: true, signedIn: false },
    edge: { installed: true, signedIn: false },
    notification: {
      level: 'error',
      app: 'webExtension',
      status: 'offline',
      settingsTab: 'chrome',
      message:
        'The Chrome and Edge extensions are not signed in. Please sign in immediately to ensure web activities are captured.',
    },
  },
  {
    chrome: { installed: true, signedIn: false },
    edge: { installed: true, signedIn: true },
    notification: {
      level: 'warning',
      app: 'webExtension',
      status: 'offline',
      settingsTab: 'chrome',
      message:
        'The Chrome extension is not signed in. Please sign in to ensure web activities are captured.',
    },
  },
  {
    chrome: { installed: true, signedIn: true },
    edge: { installed: true, signedIn: false },
    notification: {
      level: 'warning',
      app: 'webExtension',
      status: 'offline',
      settingsTab: 'edge',
      message:
        'The Edge extension is not signed in. Please sign in to ensure web activities are captured.',
    },
  },
]

const toAliveAppStatus = (status: unknown): AliveAppStatus | null => {
  if (status === 'online' || status === 'offline' || status === 'uninstall') {
    return status
  }

  return null
}

const toAliveAppState = (status: AliveAppStatus): AliveAppState => {
  return {
    installed: status !== 'uninstall',
    signedIn: status === 'online',
  }
}

const matchesAliveAppState = (state: AliveAppState, target: AliveAppState) =>
  state.installed === target.installed && state.signedIn === target.signedIn

const sortNotifications = (
  notifications: Array<AliveAppsNotification | null>
): AliveAppsNotification[] => {
  const visibleNotifications = notifications.filter(
    (notification): notification is AliveAppsNotification =>
      notification !== null
  )

  return visibleNotifications.sort((first, second) => {
    if (first.status !== second.status) {
      return first.status === 'uninstall' ? -1 : 1
    }

    if (first.app !== second.app) {
      return first.app === 'desktopAgent' ? -1 : 1
    }

    return 0
  })
}

const getWebExtensionNotification = (
  chromeStatus: AliveAppStatus | null,
  edgeStatus: AliveAppStatus | null
): AliveAppsNotification | null => {
  if (!chromeStatus || !edgeStatus) return null

  const chrome = toAliveAppState(chromeStatus)
  const edge = toAliveAppState(edgeStatus)
  const matchedRule = WEB_EXTENSION_NOTIFICATION_RULES.find(
    rule =>
      matchesAliveAppState(chrome, rule.chrome) &&
      matchesAliveAppState(edge, rule.edge)
  )

  return matchedRule?.notification ?? null
}

const getDesktopAgentNotification = (
  agentEnabled: boolean,
  desktopStatus: AliveAppStatus | null
): AliveAppsNotification | null => {
  if (!agentEnabled || !desktopStatus || desktopStatus === 'online') return null

  if (desktopStatus === 'uninstall') {
    return DESKTOP_AGENT_UNINSTALLED_NOTIFICATION
  }

  return DESKTOP_AGENT_SIGNED_OUT_NOTIFICATION
}

export const getAliveAppsNotifications = ({
  agentEnabled,
  desktopAgentStatus,
  chromeExtensionStatus,
  edgeExtensionStatus,
}: GetAliveAppsNotificationParams): AliveAppsNotification[] => {
  const webExtensionNotification = getWebExtensionNotification(
    toAliveAppStatus(chromeExtensionStatus),
    toAliveAppStatus(edgeExtensionStatus)
  )
  const desktopAgentNotification = getDesktopAgentNotification(
    agentEnabled,
    toAliveAppStatus(desktopAgentStatus)
  )

  return sortNotifications([
    webExtensionNotification,
    desktopAgentNotification,
  ])
}
